import {
    AfterViewInit,
    ChangeDetectionStrategy,
    Component,
    Input,
    OnInit,
    ViewChild,
    ViewEncapsulation,
} from '@angular/core';
import { EditTabModel, EditTabsModel } from '../../utilities/edit-tabs/edit-tabs.model';
import { ObjectTypes } from '@fi-shared/types/object-type';
import { ObjectTitleService } from '../../utilities/fi-services/object-title.service';
import { StatisticService } from '@fi-shared/statistic/statistic.service';
import { ModuleType } from '@fi-shared/types/module-type';
import { DropdownSettingsModelV2 } from '@fi-shared/dropdown';
import { ILightWeightObject, TagModel } from '@fi-shared/models';
import { TagService } from '../tag.service';
import { LoadingService } from '@fi-shared/fi-loading/loading.service';
import { SaveChangesWarningService } from '@fi-shared/fi-shared-services/save-changes-warning.service';
import { AccessRightFilterSettings } from '@fi-shared/access-right-filters';
import {
    ITagDialogProcessUntagData,
    TagDialogProcessUntagComponent,
} from '../tag-dialog-process-untag/tag-dialog-process-untag.component';
import { FiStateService } from '@fi-shared/fi-state-proxy/fi-state.proxy.service';
import { AccessRightFiltersComponent } from '@fi-shared/access-right-filters/access-right-filters.component';
import { first } from 'rxjs/operators';
import { AppTextService } from '@fi-shared/app-text';
import { FiStateNames } from '@fi-shared/fi-state-proxy/fi-state-names';
import { FiDialogsService } from '@fi-shared/fi-dialogs';
import { MoreMenuMobileSettingsModel } from '@fi-app/utilities/more-menu-mobile/more-menu-mobile-settings.model';
import { IModuleTypesItem } from '@fi-shared/models/module-types';
import { EditTranslationComponent } from '@fi-app/components/edit-translation/edit-translation.component';
import { IFiDialogResult } from '@fi-shared/fi-dialogs/fi-dialogs';
import { ContentLanguagesService } from '@fi-app/utilities/fi-services/content-languages.service';
import { ILightWeightTagForList } from '@fi-app/tag/tag';
import {
    FiDropdownActionResult,
    FiDropdownActionGuard,
    FiDropdownItemDisabledFn,
} from '@fi-shared/fi-select-search/fi-select-search';
import { INameIdObj } from '@fi-shared/fi-shared-interfaces';
import { FiUserDataService } from '@fi-shared/fi-shared-services/fi-user-data.service';
import { FiContentLanguageSharedService } from '@fi-shared/fi-content-language-shared/fi-content-language-shared.service';
import { fiTextToHtml } from '@fi-shared/utils';
import { FiTagHelperService } from '../fi-tag-helper.service';

@Component({
    selector: 'fi-tag-edit',
    templateUrl: './tag-edit.component.html',
    styleUrls: ['./tag-edit.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    host: { class: 'fi-edit-tag' },
    standalone: false,
})
export class TagEditComponent implements OnInit, AfterViewInit {
    @ViewChild(AccessRightFiltersComponent, { static: false }) accessRightFilters: AccessRightFiltersComponent;
    @ViewChild(EditTranslationComponent) editTranslationComponent: EditTranslationComponent;

    @Input('tag') Tag: TagModel;
    @Input() allTags: ILightWeightTagForList[];

    editTabs: EditTabsModel;
    moreMenuItems: MoreMenuMobileSettingsModel;
    parentListData = new DropdownSettingsModelV2<ILightWeightTagForList>({
        filterBy: 'Name',
        dataValueParentField: 'ParentId',
    });
    moduleTypeListData = new DropdownSettingsModelV2<INameIdObj>({
        filterBy: 'Name',
    });
    accessRightFilterSettings: AccessRightFilterSettings;

    constructor(
        private objectTitleService: ObjectTitleService,
        private appTextService: AppTextService,
        private statisticService: StatisticService,
        private tagService: TagService,
        private fiLoadingService: LoadingService,
        private saveChangesWarningService: SaveChangesWarningService,
        private fiDialogsService: FiDialogsService,
        private stateService: FiStateService,
        private contentLanguagesService: ContentLanguagesService,
        private fiUserDataService: FiUserDataService,
        private fiContentLanguageSharedService: FiContentLanguageSharedService,
        private fiTagHelperService: FiTagHelperService,
    ) {}

    ngOnInit() {
        this.setListDataSettings();
        this.setPageHeaderData();
    }

    private setListDataSettings() {
        this.parentListData.setDataSet(this.allTags);
        this.moduleTypeListData.setDataSet(
            this.fiTagHelperService.getTagModuleTypes().map((item: IModuleTypesItem) => {
                return { Id: item.Id, Name: this.appTextService.get(item.TextId) };
            }),
        );
        this.accessRightFilterSettings = new AccessRightFilterSettings(ModuleType.Tag, false, true, ObjectTypes.Tag);
        this.statisticService.Visit(ModuleType.Tag, ObjectTypes.Tag, this.Tag.Id);
    }

    isTagDisabledFn: FiDropdownItemDisabledFn<ILightWeightTagForList> = (
        item: ILightWeightTagForList,
        allItems: ILightWeightTagForList[],
    ) => {
        // tag can't be selected as parent for itself
        if (this.Tag.Id === item.Id) return true;

        // maximum tags tree deepness can be 3
        const availableTags: number[] = allItems
            .filter((tag: ILightWeightTagForList) => tag.Level < 2)
            .map((tag: ILightWeightTagForList) => tag.Id);

        return availableTags?.length && availableTags.indexOf(item.Id) === -1;
    };

    private setPageHeaderData() {
        this.editTabs = new EditTabsModel(
            this.objectTitleService.GetTitle(this.Tag.Id, this.Tag.Name, ObjectTypes.Tag),
        );
        this.editTabs.Tabs.push(
            new EditTabModel(this.appTextService.get('common.general'), this.appTextService.get('common.general')),
        );
        this.moreMenuItems = new MoreMenuMobileSettingsModel();
        this.moreMenuItems.AddListItem(this.appTextService.get('common.select_lang'), () => {
            this.openSelectLanguageDialog();
        });
    }

    private openSelectLanguageDialog() {
        this.contentLanguagesService
            .openSelectLanguageDialog(this.editTranslationComponent.ActiveItem)
            .onYesButtonClick((result: number) => (this.editTranslationComponent.ActiveItem = result));
    }

    getDefaultContentLang(): number {
        return !!this.Tag.Id
            ? this.fiContentLanguageSharedService.getContentLanguageIdByContentTranslation(this.Tag.Names)
            : this.fiUserDataService.getContentLanguage();
    }

    ngAfterViewInit(): void {
        const getModelData = () => {
            return {
                tag: this.Tag,
                accessRightFilterSettings: this.accessRightFilterSettings,
            };
        };

        this.accessRightFilters.onInitialized$.pipe(first()).subscribe(() => {
            this.saveChangesWarningService.start(getModelData);
        });
    }

    save() {
        this.fiLoadingService.trackPromise(
            this.tagService.SaveTag(this.Tag).then((data: number) => {
                if (data) {
                    this.accessRightFilters.OnAccessRightFilterCollectionSaved();

                    this.saveChangesWarningService.stop();
                    this.stateService.go(FiStateNames.Tag.List);
                } else {
                    alert('Something went wrong! ' + data);
                }
            }),
        );
    }

    canSelectParentTagFn: FiDropdownActionGuard<ILightWeightTagForList> = ($event) => {
        // https://chainformation.atlassian.net/browse/FI-6016
        // user can select parent tag even if it has tagged items
        return Promise.resolve<FiDropdownActionResult>({ result: true });
        /*return new Promise<FiDropdownActionResult>((resolve, reject) => {
            this.parentListDataOnClick($event.item).then(() => resolve({ result: true }), () => resolve({ result: false }));
        })*/
    };

    private parentListDataOnClick(item: ILightWeightTagForList) {
        return new Promise<void>((resolve, reject) => {
            this.processMainTagChange().then(() => {
                this.processUntagging(item).then(() => {
                    this.Tag.ParentSiteMapsCount = 0;
                    resolve();
                }, reject);
            }, reject);
        });
    }

    private processMainTagChange() {
        return new Promise<void>((resolve, reject) => {
            if (!this.Tag.ParentSiteMapsCount) {
                resolve();
            } else {
                const dialogContentData: ITagDialogProcessUntagData = {
                    step1: {
                        message: String.format(
                            this.appTextService.get('confirm_tag_sitemap_change_text_1'),
                            this.Tag.ParentSiteMapsCount,
                        ),
                        buttonText: this.appTextService.get('change_main_tag'),
                    },
                    step2: {
                        message: String.format(
                            this.appTextService.get('confirm_tag_sitemap_change_text_2'),
                            this.Tag.ParentSiteMapsCount,
                        ),
                        buttonText: this.appTextService.get('ok'),
                        confirmTextToInput2: this.appTextService.get('confirm_tag_sitemap_change_text_to_input'),
                    },
                };
                this.openUntagDialog(dialogContentData)
                    .onYesButtonClick(() => resolve())
                    .onNoButtonClick(reject);
            }
        });
    }

    private openUntagDialog(dialogContentData: ITagDialogProcessUntagData): IFiDialogResult<boolean> {
        return this.fiDialogsService.custom({
            headerText: this.appTextService.get('common.confirm'),
            contentComponentRef: TagDialogProcessUntagComponent,
            contentComponentData: dialogContentData,
            config: {
                maxWidth: '400px',
            },
        });
    }

    private processUntagging(item: ILightWeightTagForList) {
        return new Promise<void>((resolve, reject) => {
            if (!item.UsagesCount) {
                resolve();
            } else {
                const dialogContentData: ITagDialogProcessUntagData = {
                    step1: {
                        message: String.format(
                            this.appTextService.get(
                                item.UsagesCount === 1 ? 'confirm_untag_item_text_1' : 'confirm_untag_items_text_1',
                            ),
                            item.UsagesCount,
                            fiTextToHtml(item.Name),
                        ),
                        buttonText: String.format(
                            this.appTextService.get(item.UsagesCount === 1 ? 'untag_item' : 'untag_items'),
                            item.UsagesCount,
                        ),
                    },
                    step2: {
                        message: String.format(
                            this.appTextService.get(
                                item.UsagesCount === 1 ? 'confirm_untag_item_text_2' : 'confirm_untag_items_text_2',
                            ),
                            item.UsagesCount,
                            item.Name,
                        ),
                        buttonText: this.appTextService.get('ok'),
                        confirmTextToInput2: this.appTextService.get('confirm_untag_text_to_input_2'),
                    },
                };
                this.openUntagDialog(dialogContentData)
                    .onYesButtonClick(() => {
                        this.fiLoadingService.trackPromise(this.tagService.UntagItems(item.Id)).then(() => {
                            item.UsagesCount = 0;
                            resolve();
                        }, reject);
                    })
                    .onNoButtonClick(reject);
            }
        });
    }
}
